'use client';

import React from 'react';
import Link from 'next/link';
import { Bookmark, Check, ListPlus, Pause, Play } from 'lucide-react';
import type { PublicArticle } from '@/lib/types';
import { cn, truncate } from '@/lib/utils';
import { formatSupplementName, getArticleKindInfo } from '@/lib/articles';
import { isIngredientLikeSupplementKey } from '@/lib/aviado/supplement-indexing';
import { TierChip } from './TierChip';
import { useAudio, type QueueTrack } from '@/lib/contexts/ListenQueueContext.shim';

/**
 * ArticleCard — three editorial variants.
 *
 *   - `card`        → Latest/home + article grids. Thin top rule, no photo.
 *   - `pick`        → Editor's picks row (vertical rule dividers in parent).
 *                     First child can pass `isHero` to enlarge the title.
 *   - `result-row`  → Browse inline list row with date column + actions.
 *
 * Old variants `default`/`horizontal` map to `card` for backward compatibility
 * during Phase 3→5 migration. Call sites should migrate to `card` explicitly.
 *
 * Spec sources: design-references/index.html (.card, .pick, .pick--hero),
 * design-references/browse.html (.result).
 */

/**
 * @see {@link ArticleCard} — three editorial variants.
 * `default` and `horizontal` are legacy aliases of `card` kept through Phase
 * 3→5 migration. New call sites should use `card` directly.
 */
export type ArticleCardVariant =
  | 'card'
  | 'pick'
  | 'result-row'
  /** @deprecated Use `card`. */
  | 'default'
  /** @deprecated Use `card`. Horizontal layout dropped in v3.0 redesign. */
  | 'horizontal';

export type ArticleCardArticle = Pick<
  PublicArticle,
  | 'id'
  | 'slug'
  | 'title'
  | 'subtitle'
  | 'dek'
  | 'supplement'
  | 'kind'
  | 'evidence_tier'
  | 'published_at'
  | 'created_at'
  | 'reading_time_minutes'
  | 'sources_count'
  | 'author'
  | 'audio_url'
  | 'audio_duration_seconds'
>;

export interface ArticleCardProps {
  article: ArticleCardArticle;
  variant?: ArticleCardVariant;
  /** Display ordinal for `pick` variant (shows "Pick 0N"). */
  pickIndex?: number;
  /** Render `pick` variant with the hero-sized title (first pick in a row). */
  isHero?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function ArticleCard(props: ArticleCardProps) {
  const { variant = 'card', ...rest } = props;
  switch (variant) {
    case 'pick':
      return <ArticleCardPick {...rest} />;
    case 'result-row':
      return <ArticleCardResultRow {...rest} />;
    case 'card':
    case 'default':
    case 'horizontal':
    default:
      return <ArticleCardEditorial {...rest} />;
  }
}

// ----- card (default editorial) -----

function ArticleCardEditorial({
  article,
  className,
  style,
}: Omit<ArticleCardProps, 'variant' | 'pickIndex' | 'isHero'>) {
  const kindInfo = getArticleKindInfo(article.kind);
  return (
    <article
      className={cn(
        'group block pt-4 border-t border-border-strong',
        'transition-colors duration-fast text-ink',
        className
      )}
      style={style}
    >
      {/* Header: supplement · kind + tier chip */}
      <div className="flex justify-between items-center mb-3.5">
        <span className="font-mono text-[10.5px] tracking-[0.12em] uppercase text-slate">
          <SupplementNameLink supplement={article.supplement} />{' '}
          · {kindInfo.label}
        </span>
        <TierChip tier={article.evidence_tier} />
      </div>

      {/* Title */}
      <Link
        href={`/articles/${article.slug}/`}
        className="block text-ink no-underline transition-colors duration-fast group-hover:text-aviado-blue"
      >
        <h3
          className={cn(
            'font-heading font-semibold text-[20px] leading-[1.22] tracking-[-0.01em]',
            'm-0 mb-3'
          )}
          style={{ fontVariationSettings: '"opsz" 22' }}
        >
          {article.title}
        </h3>
      </Link>

      {/* Dek */}
      {(article.dek || article.subtitle) && (
        <p className="font-sans text-[14px] leading-[1.55] text-slate m-0 mb-4">
          {truncate(article.dek ?? article.subtitle ?? '', 160)}
        </p>
      )}

      {/* Meta + actions */}
      <div className="flex justify-between items-center font-mono text-[11px] text-slate tracking-[0.02em]">
        <span>
          {article.reading_time_minutes} min · {article.sources_count} sources
        </span>
        <div className="flex gap-1.5 items-center">
          <PlayButton article={article} />
          <QueueButton article={article} />
          <BookmarkButton articleId={article.id} />
        </div>
      </div>
    </article>
  );
}

// ----- pick (editor's picks row) -----

function ArticleCardPick({
  article,
  pickIndex,
  isHero,
  className,
  style,
}: Omit<ArticleCardProps, 'variant'>) {
  const kindInfo = getArticleKindInfo(article.kind);
  const pickLabel =
    pickIndex != null
      ? `Pick ${String(pickIndex).padStart(2, '0')}${isHero ? ' · ' + kindInfo.label : ''}`
      : kindInfo.label;

  return (
    <article
      className={cn(
        'group flex flex-col cursor-pointer',
        'px-0 py-7',
        // Vertical rule dividers apply only in the multi-column layout on
        // `lg` — stacked single-column on mobile would otherwise show a stray
        // right-edge rule on every card.
        'lg:pr-8 lg:border-r lg:border-border lg:last:border-r-0 lg:last:pr-0 lg:[&+&]:pl-8',
        'transition-colors duration-fast text-ink',
        className
      )}
      style={style}
    >
      {/* Eyebrow */}
      <span className="block font-mono text-[10.5px] font-medium tracking-[0.14em] uppercase text-aviado-teal-text mb-3.5">
        {pickLabel}
      </span>

      {/* Tier + supplement */}
      <div className="mb-3.5 flex flex-wrap items-center gap-2">
        <TierChip tier={article.evidence_tier} />
        <span className="font-mono text-[10.5px] tracking-[0.12em] uppercase">
          <SupplementNameLink supplement={article.supplement} />
        </span>
      </div>

      {/* Title */}
      <Link
        href={`/articles/${article.slug}/`}
        className="block text-warm-navy no-underline transition-colors duration-fast group-hover:text-aviado-blue"
      >
        <h3
          className={cn(
            'font-display font-medium tracking-[-0.012em] m-0 mb-3',
            'text-wrap-pretty',
            isHero
              ? 'text-[30px] leading-[1.14]'
              : 'text-[22px] leading-[1.22]'
          )}
          style={{ fontVariationSettings: isHero ? '"opsz" 48' : '"opsz" 36' }}
        >
          {article.title}
        </h3>
      </Link>

      {/* Dek */}
      {(article.dek || article.subtitle) && (
        <p
          className={cn(
            'font-sans leading-[1.55] text-slate m-0 mb-4',
            isHero ? 'text-[15px]' : 'text-[14px]'
          )}
        >
          {truncate(article.dek ?? article.subtitle ?? '', 180)}
        </p>
      )}

      {/* Meta + actions */}
      <div className="mt-auto pt-4 border-t border-dashed border-border flex justify-between items-center font-mono text-[11px] text-slate tracking-[0.04em]">
        <span>
          {article.reading_time_minutes} min · {article.sources_count} sources
        </span>
        <div className="flex gap-1.5 items-center">
          <PlayButton article={article} compact />
          <QueueButton article={article} compact />
        </div>
      </div>
    </article>
  );
}

// ----- result-row (browse inline) -----

function ArticleCardResultRow({
  article,
  className,
  style,
}: Omit<ArticleCardProps, 'variant' | 'pickIndex' | 'isHero'>) {
  const kindInfo = getArticleKindInfo(article.kind);
  const date = parseShortDate(article.published_at ?? article.created_at);
  return (
    <article
      className={cn(
        'group grid grid-cols-[120px_1fr_200px] gap-8 items-start',
        'py-6 border-t border-border last:border-b text-ink',
        'transition-[padding,background-color] duration-fast',
        'hover:pl-2 hover:bg-white/50',
        className
      )}
      style={style}
    >
      {/* Date column */}
      <div className="font-mono text-[11px] tracking-[0.04em] text-slate pt-1">
        <strong
          className="block font-heading font-medium text-[14px] text-ink mb-0.5"
          style={{ fontVariationSettings: '"opsz" 16' }}
        >
          {date.monthDay}
        </strong>
        <span>{date.year}</span>
      </div>

      {/* Main */}
      <div className="min-w-0">
        <div className="flex gap-2.5 items-center mb-2.5 font-mono text-[10.5px] tracking-[0.12em] uppercase text-slate">
          <SupplementNameLink supplement={article.supplement} />
          <span className="opacity-40">·</span>
          <span>{kindInfo.label}</span>
          <span className="opacity-40">·</span>
          <TierChip tier={article.evidence_tier} size="sm" />
        </div>
        <Link
          href={`/articles/${article.slug}/`}
          className="block text-warm-navy no-underline transition-colors duration-fast group-hover:text-aviado-blue"
        >
          <h3
            className={cn(
              'font-display font-medium text-[24px] leading-[1.2] tracking-[-0.012em]',
              'm-0 mb-2'
            )}
            style={{ fontVariationSettings: '"opsz" 32' }}
          >
            {article.title}
          </h3>
        </Link>
        {(article.dek || article.subtitle) && (
          <p className="font-sans text-[14.5px] leading-[1.55] text-slate m-0">
            {truncate(article.dek ?? article.subtitle ?? '', 200)}
          </p>
        )}
      </div>

      {/* Right column */}
      <div className="flex flex-col items-end gap-2.5 pt-1 font-mono text-[11px] tracking-[0.04em] text-slate text-right">
        <div>
          {article.reading_time_minutes} min · {article.sources_count} sources
          <br />
          <span className="opacity-70">{article.author}</span>
        </div>
        <div className="flex gap-1.5 items-center">
          <PlayButton article={article} />
          <QueueButton article={article} />
          <BookmarkButton articleId={article.id} />
        </div>
      </div>
    </article>
  );
}

function SupplementNameLink({ supplement }: { supplement: string }) {
  const label = formatSupplementName(supplement);
  if (!isIngredientLikeSupplementKey(supplement)) {
    return <strong className="text-warm-navy font-medium">{label}</strong>;
  }

  return (
    <Link
      href={`/supplements/${supplement.replace(/_/g, '-')}/`}
      className="text-warm-navy font-medium no-underline hover:text-aviado-blue transition-colors duration-fast"
    >
      {label}
    </Link>
  );
}

// ---------- Shared action buttons ----------

function trackFromArticle(article: ArticleCardArticle): QueueTrack | null {
  if (!article.audio_url) return null;
  return {
    id: article.id,
    slug: article.slug,
    title: article.title,
    supplement: article.supplement,
    audioUrl: article.audio_url,
    evidenceTier: article.evidence_tier,
    durationSeconds: article.audio_duration_seconds,
  };
}

export function PlayButton({ article, compact = false }: { article: ArticleCardArticle; compact?: boolean }) {
  const { currentTrack, isPlaying, playTrack, togglePlayPause } = useAudio();
  const track = trackFromArticle(article);
  if (!track) return null;

  const isCurrent = currentTrack?.id === article.id;
  const isThisPlaying = isCurrent && isPlaying;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isCurrent) togglePlayPause();
    else playTrack(track);
  };

  const label = isThisPlaying ? 'Pause' : isCurrent ? 'Resume' : 'Play audio';

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={isThisPlaying}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex min-h-12 min-w-12 items-center justify-center gap-1 cursor-pointer rounded-md border',
        'bg-warm-navy text-white border-warm-navy',
        'hover:bg-aviado-blue hover:border-aviado-blue',
        'transition-colors duration-fast',
        compact ? 'px-2.5 py-2 text-[10.5px]' : 'px-2.5 py-2 text-[11px]'
      )}
    >
      {React.createElement(isThisPlaying ? Pause : Play, {
        className: compact ? 'w-[11px] h-[11px]' : 'w-[12px] h-[12px]',
        fill: 'currentColor',
        strokeWidth: 0,
      })}
      {compact && <span>{isThisPlaying ? 'Pause' : 'Play'}</span>}
    </button>
  );
}

export function QueueButton({ article, compact = false }: { article: ArticleCardArticle; compact?: boolean }) {
  const { addToQueue, isInQueue } = useAudio();
  const track = trackFromArticle(article);
  if (!track) return null;
  const inQueue = isInQueue(article.id);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!inQueue) addToQueue(track);
  };

  const label = inQueue ? 'Queued' : 'Add to listen queue';

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={inQueue}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex min-h-12 min-w-12 items-center justify-center gap-1 cursor-pointer rounded-md border',
        'transition-colors duration-fast',
        compact ? 'px-2.5 py-2 text-[10.5px]' : 'px-2.5 py-2 text-[11px]',
        inQueue
          ? 'bg-aviado-teal/10 text-aviado-teal-text border-aviado-teal/40'
          : 'bg-white text-warm-navy border-border hover:border-border-strong hover:text-aviado-blue'
      )}
    >
      {React.createElement(inQueue ? Check : ListPlus, {
        className: compact ? 'w-[11px] h-[11px]' : 'w-[12px] h-[12px]',
        strokeWidth: inQueue ? 2.5 : 2,
      })}
      {compact && <span>{inQueue ? 'Queued' : 'Queue'}</span>}
    </button>
  );
}

const BOOKMARKS_KEY = 'aviado_bookmarks_v1';

function readBookmarks(): string[] {
  try {
    const raw = localStorage.getItem(BOOKMARKS_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
}

function BookmarkButton({ articleId }: { articleId: string }) {
  const [saved, setSaved] = React.useState(false);

  // Hydrate + subscribe to cross-tab changes so a bookmark in tab A flips
  // the heart in tab B without a refresh.
  React.useEffect(() => {
    setSaved(readBookmarks().includes(articleId));
    const onStorage = (e: StorageEvent) => {
      if (e.key !== BOOKMARKS_KEY) return;
      setSaved(readBookmarks().includes(articleId));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [articleId]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const ids = readBookmarks();
      const next = ids.includes(articleId)
        ? ids.filter((x) => x !== articleId)
        : [...ids, articleId];
      localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(next));
      setSaved(next.includes(articleId));
    } catch {}
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={saved}
      aria-label={saved ? 'Remove bookmark' : 'Bookmark article'}
      className={cn(
        'inline-flex min-h-12 min-w-12 items-center justify-center bg-transparent border-0 cursor-pointer rounded-xs',
        'transition-colors duration-fast',
        saved ? 'text-aviado-teal-text' : 'text-slate hover:text-aviado-blue'
      )}
    >
      <Bookmark className="w-[14px] h-[14px]" strokeWidth={2} fill={saved ? 'currentColor' : 'none'} />
    </button>
  );
}

// ---------- Utilities ----------

function parseShortDate(iso?: string | null): { monthDay: string; year: string } {
  if (!iso) return { monthDay: '', year: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { monthDay: '', year: '' };
  return {
    monthDay: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    year: String(d.getFullYear()),
  };
}

export default ArticleCard;
